const STORAGE_KEY = "feedants_attribution";

export function captureAttributionFromUrl() {
    try {
        if (typeof window === "undefined") { return };

        // Some very strict browsers/extensions can throw on localStorage
        let existing = null;
        try {
            existing = window.localStorage.getItem(STORAGE_KEY);
        } catch (e) {
            console.warn("Attribution: localStorage unavailable, skipping.", e);
            return;
        }

        // Don't override if we already have first-touch attribution
        if (existing) { return };

        const params = new URLSearchParams(window.location.search);

        const data = {
            source: params.get("utm_source"),
            medium: params.get("utm_medium"),
            campaign: params.get("utm_campaign"),
            campaign_id: params.get("utm_campaign_id"),
            adset: params.get("utm_adset"),
            adset_id: params.get("utm_adset_id"),
            ad: params.get("utm_ad"),
            ad_id: params.get("utm_ad_id"),
        };

        let hasAny = false;
        Object.keys(data).forEach(function (key) {
            if (data[key]) { hasAny = true };
        });
        if (!hasAny) { return };

        data.first_visit_at = new Date().toISOString();
        data.source_type = "META_WEB";

        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (e) {
            console.warn("Attribution: failed to write localStorage, skipping.", e);
        }
    } catch (e) {
        console.error("Attribution: unexpected error, safely ignored.", e);
    }
}

export function getStoredAttribution() {
    try {
        if (typeof window === "undefined") { return null };
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) { return null };

        try {
            return JSON.parse(raw);
        } catch (e) {
            console.error("Failed to parse attribution from localStorage", e);
            return null;
        }
    } catch (e) {
        console.error("Attribution: unable to read from localStorage", e);
        return null;
    }
}

/**
 * Utility: Build deep, Play Store, and market links with referral + UTM data.
 * - Fetches attribution (utm_source, utm_medium, utm_campaign, etc.)
 * - Keeps everything inside the `referrer` param (for Play Install Referrer tracking)
 * @returns { appLink: string, playStoreLink: string, marketLink: string, intentUrl: string }
 */
export function getPlaystoreLinks(referCode) {
    const fullPackageName = "com.feedants";
    const scheme = "feedants";

    const attribution = getStoredAttribution();
    const referrerParams = new URLSearchParams();

    if (referCode) {
        referrerParams.append("referral", referCode);
    }

    if (attribution) {
        const utmData = {
            utm_source: attribution.source || "",
            utm_medium: attribution.medium || "",
            utm_campaign: attribution.campaign || "",
            utm_campaign_id: attribution.campaign_id || "",
            utm_adset: attribution.adset || "",
            utm_adset_id: attribution.adset_id || "",
            utm_ad: attribution.ad || "",
            utm_ad_id: attribution.ad_id || "",
        };
        Object.entries(utmData).forEach(([key, value]) => {
            if (value) {
                referrerParams.append(key, value);
            }
        });
    }

    const encodedReferrer = encodeURIComponent(referrerParams.toString());

    const appLink = referCode
        ? `${scheme}://referral/${encodeURIComponent(referCode)}`
        : `${scheme}://`;

    const playStoreLink = `https://play.google.com/store/apps/details?id=${fullPackageName}&referrer=${encodedReferrer}`;
    const marketLink = `market://details?id=${fullPackageName}&referrer=${encodedReferrer}`;

    // Intent link (for Android deep fallback)
    const intentUrl = `intent://${appLink.split("://")[1] || ""}#Intent;scheme=${scheme};package=${fullPackageName};S.browser_fallback_url=${encodeURIComponent(
        marketLink
    )};S.market_referrer=${encodedReferrer};end`;

    return { appLink, playStoreLink, marketLink, intentUrl };
}